import { getZoomOptions, getApertureOptions, getIrLightOptions, getIrCutFilterOptions } from '@/api/controlUnitApi'
import { Client } from '@/api/controlUnitApi/client'
import { putZoomAbsolute, putApertureAbsolute, putIrLightAbsolute, putIrCutFilter } from '@/api/controlUnitApi/sdk.gen'
import { CameraPosition, IrLightPosition } from '@/api/controlUnitApi/types.gen'
import { notifications } from '@mantine/notifications'
import { QueryKey, useMutation, useQueries, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { HARDWARE_OTHER_PANEL_REFETCH_INTERVAL_MS } from '@/constants'
import { extendKey } from '@/utils/queryKey'
import { CAMERA_KEYS, IR_LIGHT_KEYS, IR_LIGHT_POSITIONS } from './helpers'

const refetchInterval = HARDWARE_OTHER_PANEL_REFETCH_INTERVAL_MS

const byPosition = <P extends string, V>(positions: P[], values: (V | undefined)[]) =>
  Object.fromEntries(positions.map((position, index) => [position, values[index]])) as Partial<Record<P, V>>

const keysByPosition = <P extends string>(positions: P[], options: { queryKey: QueryKey }[]) =>
  Object.fromEntries(positions.map((position, index) => [position, options[index].queryKey])) as Record<P, QueryKey>

function useOtherPanelData({
  client,
  source,
  cameras,
  isRefetchingCombinedView,
}: {
  client: Client
  source: string
  cameras: CameraPosition[]
  isRefetchingCombinedView: boolean
}) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('hardware')

  const zoomOptions = useMemo(
    () => cameras.map(position => extendKey(getZoomOptions({ path: { camera_position: position }, client }), source)),
    [cameras, client, source]
  )
  const apertureOptions = useMemo(
    () =>
      cameras.map(position => extendKey(getApertureOptions({ path: { camera_position: position }, client }), source)),
    [cameras, client, source]
  )
  const irCutFilterOptions = useMemo(
    () =>
      cameras.map(position =>
        extendKey(getIrCutFilterOptions({ path: { camera_position: position }, client }), source)
      ),
    [cameras, client, source]
  )
  const irLightOptions = useMemo(
    () =>
      IR_LIGHT_POSITIONS.map(position =>
        extendKey(getIrLightOptions({ path: { ir_light_position: position }, client }), source)
      ),
    [client, source]
  )

  const zoomQueries = useQueries({ queries: zoomOptions.map(options => ({ ...options, refetchInterval })) })
  const apertureQueries = useQueries({ queries: apertureOptions.map(options => ({ ...options, refetchInterval })) })
  const irCutFilterQueries = useQueries({
    queries: irCutFilterOptions.map(options => ({ ...options, refetchInterval })),
  })
  const irLightQueries = useQueries({ queries: irLightOptions.map(options => ({ ...options, refetchInterval })) })

  const zoomLevels = byPosition(
    cameras,
    zoomQueries.map(query => query.data?.level)
  )
  const apertureLevels = byPosition(
    cameras,
    apertureQueries.map(query => query.data?.level)
  )
  const irCutFilters = byPosition(
    cameras,
    irCutFilterQueries.map(query => query.data?.enabled)
  )
  const irLightLevels = byPosition(
    IR_LIGHT_POSITIONS,
    irLightQueries.map(query => query.data?.level)
  )

  const zoomKeys = keysByPosition(cameras, zoomOptions)
  const apertureKeys = keysByPosition(cameras, apertureOptions)
  const irCutFilterKeys = keysByPosition(cameras, irCutFilterOptions)
  const irLightKeys = keysByPosition(IR_LIGHT_POSITIONS, irLightOptions)

  const refetchAll = useCallback(() => {
    const allOptions = [...zoomOptions, ...apertureOptions, ...irCutFilterOptions, ...irLightOptions]
    allOptions.forEach(({ queryKey }) => queryClient.invalidateQueries({ queryKey }))
  }, [zoomOptions, apertureOptions, irCutFilterOptions, irLightOptions, queryClient])

  useEffect(() => {
    if (isRefetchingCombinedView) {
      refetchAll()
    }
  }, [isRefetchingCombinedView, refetchAll])

  const levelMutationOptions = <TVariables extends { position: string }>({
    mutationFn,
    queryKeyFor,
    nextData,
    title,
    successMessage,
    failureMessage,
  }: {
    mutationFn: (variables: TVariables) => Promise<unknown>
    queryKeyFor: (variables: TVariables) => QueryKey
    nextData: (variables: TVariables) => unknown
    title: string
    successMessage: (variables: TVariables) => string
    failureMessage: (variables: TVariables) => string
  }) => ({
    mutationFn,
    onMutate: async (variables: TVariables) => {
      const queryKey = queryKeyFor(variables)
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData(queryKey)
      queryClient.setQueryData(queryKey, nextData(variables))
      return { queryKey, previous }
    },
    onError: (
      _error: unknown,
      variables: TVariables,
      snapshot: { queryKey: QueryKey; previous: unknown } | undefined
    ) => {
      if (snapshot) queryClient.setQueryData(snapshot.queryKey, snapshot.previous)
      notifications.show({ title, message: failureMessage(variables), color: 'red' })
    },
    onSuccess: (
      _response: unknown,
      variables: TVariables,
      snapshot: { queryKey: QueryKey; previous: unknown } | undefined
    ) => {
      queryClient.invalidateQueries({ queryKey: snapshot?.queryKey ?? queryKeyFor(variables) })
      notifications.show({ title, message: successMessage(variables), color: 'green' })
    },
  })

  const { mutate: mutateZoom } = useMutation(
    levelMutationOptions({
      mutationFn: ({ position, value }: { position: CameraPosition; value: number }) =>
        putZoomAbsolute({
          body: { level: value },
          path: { camera_position: position },
          client,
          throwOnError: true,
        }),
      queryKeyFor: ({ position }) => zoomKeys[position],
      nextData: ({ position, value }) => ({ camera_position: position, level: value }),
      title: t('zoomValue'),
      successMessage: ({ position }) => t('zoomValueSet', { camera: t(CAMERA_KEYS[position].name) }),
      failureMessage: ({ position }) => t('failedToSetZoomValue', { camera: t(CAMERA_KEYS[position].name) }),
    })
  )

  const { mutate: mutateAperture } = useMutation(
    levelMutationOptions({
      mutationFn: ({ position, value }: { position: CameraPosition; value: number }) =>
        putApertureAbsolute({
          body: { level: value },
          path: { camera_position: position },
          client,
          throwOnError: true,
        }),
      queryKeyFor: ({ position }) => apertureKeys[position],
      nextData: ({ position, value }) => ({ camera_position: position, level: value }),
      title: t('apertureValue'),
      successMessage: ({ position }) => t('apertureValueSet', { camera: t(CAMERA_KEYS[position].name) }),
      failureMessage: ({ position }) => t('failedToSetApertureValue', { camera: t(CAMERA_KEYS[position].name) }),
    })
  )

  const { mutate: mutateIrLight } = useMutation(
    levelMutationOptions({
      mutationFn: ({ position, value }: { position: IrLightPosition; value: number }) =>
        putIrLightAbsolute({
          body: { level: value },
          path: { ir_light_position: position },
          client,
          throwOnError: true,
        }),
      queryKeyFor: ({ position }) => irLightKeys[position],
      nextData: ({ position, value }) => ({ ir_light_position: position, level: value }),
      title: t('infraredValue'),
      successMessage: ({ position }) => t('infraredValueSet', { camera: t(IR_LIGHT_KEYS[position].name) }),
      failureMessage: ({ position }) => t('failedToSetInfraredValue', { camera: t(IR_LIGHT_KEYS[position].name) }),
    })
  )

  const { mutate: mutateIrCutFilter } = useMutation(
    levelMutationOptions({
      mutationFn: ({ position, enabled }: { position: CameraPosition; enabled: boolean }) =>
        putIrCutFilter({
          body: { enabled },
          path: { camera_position: position },
          client,
          throwOnError: true,
        }),
      queryKeyFor: ({ position }) => irCutFilterKeys[position],
      nextData: ({ position, enabled }) => ({ camera_position: position, enabled }),
      title: t('irCutFilter'),
      successMessage: ({ position }) => t('irCutFilterSet', { camera: t(CAMERA_KEYS[position].name) }),
      failureMessage: ({ position }) => t('failedToSetIrCutFilter', { camera: t(CAMERA_KEYS[position].name) }),
    })
  )

  const isRefetching = [...zoomQueries, ...apertureQueries, ...irCutFilterQueries, ...irLightQueries].some(
    query => query.isRefetching
  )

  return {
    zoomLevels,
    apertureLevels,
    irLightLevels,
    irCutFilters,
    mutateZoom,
    mutateAperture,
    mutateIrLight,
    mutateIrCutFilter,
    isRefetching,
  }
}

export default useOtherPanelData
